/**
 * App updates wait while the suite is performing (2026-09-30,
 * next-update-plan 1.6).
 *
 * WHAT WAS WRONG: the service worker was registered `autoUpdate` - a new
 * deploy installed, took over at once and RELOADED the page by itself, and any
 * reload mid-gig came back on the newest bundle, possibly one never
 * soundchecked. Now it is registered `prompt` (vite.config.ts): a new version
 * installs and WAITS, and this module decides when it applies.
 *
 * THE RULE (`mayApplyUpdate`): apply automatically only when performance mode
 * is KNOWN to be off. "Not known yet" is not "off" - right after a reload
 * mid-gig the app has not heard OPBridge's switch for a second or two, and
 * with the venue network down it may not hear it at all. Holding an update is
 * harmless; applying one on stage is not. The operator can always apply a
 * held update by hand (`applyAppUpdateNow`, the logo menu's "Update now").
 *
 * Outside performance mode nothing changes for the operator: a new version
 * applies as soon as it is ready, the way `autoUpdate` did.
 *
 * Production only: in dev `virtual:pwa-register` is a stub (no service
 * worker; `devOptions.enabled` is false), so none of this fires at the desk.
 */

import { createSignal, createEffect, createRoot, type Accessor } from 'solid-js';
import { registerSW } from 'virtual:pwa-register';
import { performanceMode, performanceModeKnown } from './opbridge/performanceMode.ts';
import { anyTimelineRunning } from './timeline/transport.ts';
import { leaveOnPurpose } from './navigate/leaveGuard.ts';

const [readySig, setReady] = createSignal(false);

/** True while a new version is installed and waiting to be applied. */
export const appUpdateReady: Accessor<boolean> = readySig;

/** May a waiting update reload the app now? Pure, so every case is testable. Not while a timeline plays either
 *  (2026-11-03, with the leave guard: a reload mid-song is the same interruption, performance mode or not). */
export function mayApplyUpdate(performanceKnown: boolean, performing: boolean, timelinePlaying = false): boolean {
  return performanceKnown && !performing && !timelinePlaying;
}

let applyUpdate: ((reloadPage?: boolean) => Promise<void>) | null = null;
let applying = false;

/** Apply a waiting update now (reloads the app). The operator's override. */
export function applyAppUpdateNow(): void {
  if (!applyUpdate || applying) return;
  applying = true;
  // Its reload is ours: the leave guard does not ask about it.
  leaveOnPurpose();
  void applyUpdate(true).finally(() => { applying = false; });
}

let installed = false;

/** Register the service worker and gate its updates. Idempotent; called once
 *  from main.tsx. */
export function installAppUpdates(): void {
  if (installed) return;
  installed = true;
  applyUpdate = registerSW({
    immediate: true,
    onNeedRefresh() { setReady(true); },
  });
  createRoot(() => {
    createEffect(() => {
      if (readySig() && mayApplyUpdate(performanceModeKnown(), performanceMode(), anyTimelineRunning())) applyAppUpdateNow();
    });
  });
}
